import { useState, useEffect } from "react";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import { supabase, ContactSubmission } from "@/lib/supabase";

const STATUS_COLORS: Record<string, string> = {
  new: "bg-[#e05a8a]/10 text-[#e05a8a]",
  read: "bg-[#3a8cbf]/10 text-[#3a8cbf]",
  replied: "bg-green-100 text-green-700",
};

const STATUS_LABELS: Record<string, string> = {
  new: "New",
  read: "Read",
  replied: "Replied",
};

const ContactDetailModal = ({
  submission,
  onClose,
  onStatusChange,
}: {
  submission: ContactSubmission;
  onClose: () => void;
  onStatusChange: (id: string, status: ContactSubmission["status"]) => void;
}) => {
  const [updating, setUpdating] = useState(false);

  const handleStatus = async (status: ContactSubmission["status"]) => {
    setUpdating(true);
    await supabase.from("contact_submissions").update({ status }).eq("id", submission.id);
    onStatusChange(submission.id, status);
    setUpdating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Message from {submission.name}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {new Date(submission.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer transition-colors">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wide">Name</p>
              <p className="text-sm font-semibold text-gray-900">{submission.name}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wide">Email</p>
              <a href={`mailto:${submission.email}`} className="text-sm font-semibold text-[#3a8cbf] hover:underline cursor-pointer">
                {submission.email}
              </a>
            </div>
            {submission.phone && (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wide">Phone</p>
                <p className="text-sm font-semibold text-gray-900">{submission.phone}</p>
              </div>
            )}
            {submission.subject && (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wide">Subject</p>
                <p className="text-sm font-semibold text-gray-900">{submission.subject}</p>
              </div>
            )}
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Message</p>
            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{submission.message}</p>
          </div>

          <div>
            <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Update Status</p>
            <div className="flex gap-2 flex-wrap">
              {(["new", "read", "replied"] as const).map((s) => (
                <button
                  key={s}
                  disabled={updating || submission.status === s}
                  onClick={() => handleStatus(s)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                    submission.status === s
                      ? STATUS_COLORS[s] + " ring-2 ring-offset-1 ring-current"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {STATUS_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <a
              href={`mailto:${submission.email}?subject=Re: ${submission.subject || "Your Inquiry"}`}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white text-sm font-semibold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
              onClick={() => handleStatus("replied")}
            >
              <i className="ri-reply-line" /> Reply via Email
            </a>
            {submission.phone && (
              <a
                href={`https://wa.me/${submission.phone.replace(/\D/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-green-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line" /> WhatsApp
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminContactsPage = () => {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ContactSubmission | null>(null);
  const [filter, setFilter] = useState<"all" | "new" | "read" | "replied">("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await supabase
        .from("contact_submissions")
        .select("*")
        .order("created_at", { ascending: false });
      setSubmissions(data || []);
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleStatusChange = (id: string, status: ContactSubmission["status"]) => {
    setSubmissions((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
    if (selected?.id === id) setSelected((prev) => prev ? { ...prev, status } : prev);
  };

  const handleOpen = async (submission: ContactSubmission) => {
    setSelected(submission);
    if (submission.status === "new") {
      await supabase.from("contact_submissions").update({ status: "read" }).eq("id", submission.id);
      handleStatusChange(submission.id, "read");
    }
  };

  const filtered = submissions.filter((s) => {
    const matchStatus = filter === "all" || s.status === filter;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      s.name.toLowerCase().includes(q) ||
      s.email.toLowerCase().includes(q) ||
      (s.subject || "").toLowerCase().includes(q) ||
      s.message.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const newCount = submissions.filter((s) => s.status === "new").length;

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Contact Submissions
              {newCount > 0 && (
                <span className="text-sm px-2 py-0.5 rounded-full bg-[#e05a8a]/10 text-[#e05a8a] font-semibold">{newCount} new</span>
              )}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">Messages from the contact form</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 flex-shrink-0">
            {(["all", "new", "read", "replied"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  filter === f ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {f === "all" ? "All" : STATUS_LABELS[f]}
                {f !== "all" && (
                  <span className="ml-1.5 text-xs opacity-60">{submissions.filter((s) => s.status === f).length}</span>
                )}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, message..."
              className="w-full pl-9 pr-4 py-2 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#3a8cbf] transition-colors"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-10 h-10 border-4 border-[#3a8cbf]/20 border-t-[#3a8cbf] rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gray-50 mx-auto mb-4">
              <i className="ri-mail-line text-2xl text-gray-300" />
            </div>
            <p className="text-gray-400 text-sm">No submissions found</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">From</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">Subject</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider hidden md:table-cell">Message</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider hidden lg:table-cell">Date</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((s) => (
                  <tr
                    key={s.id}
                    className={`hover:bg-gray-50/80 transition-colors cursor-pointer ${s.status === "new" ? "bg-[#3a8cbf]/3" : ""}`}
                    onClick={() => handleOpen(s)}
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {s.status === "new" && <span className="w-2 h-2 rounded-full bg-[#e05a8a] flex-shrink-0" />}
                        <div className="min-w-0">
                          <p className={`text-sm text-gray-900 truncate max-w-[130px] ${s.status === "new" ? "font-semibold" : "font-medium"}`}>
                            {s.name}
                          </p>
                          <p className="text-xs text-gray-400 truncate max-w-[130px]">{s.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 hidden sm:table-cell">
                      <p className="text-sm text-gray-600 truncate max-w-[160px]">{s.subject || "—"}</p>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell">
                      <p className="text-sm text-gray-400 truncate max-w-[200px]">{s.message}</p>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[s.status]}`}>
                        {STATUS_LABELS[s.status]}
                      </span>
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell">
                      <p className="text-xs text-gray-400">
                        {new Date(s.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleOpen(s); }}
                        className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-medium hover:bg-[#3a8cbf]/10 hover:text-[#3a8cbf] transition-colors cursor-pointer whitespace-nowrap"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <ContactDetailModal submission={selected} onClose={() => setSelected(null)} onStatusChange={handleStatusChange} />
      )}
    </AdminLayout>
  );
};

export default AdminContactsPage;
